import React, { useState, useEffect } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, TextField, Stack } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const DateRangeFilter = ({
  onDateRangeChange,
  startDate,
  endDate,
  selectedPreset,
  sx,
  label = 'Date Range',
  onOpen,
  onClose,
}) => {
  const [preset, setPreset] = useState(selectedPreset || 'All');

  useEffect(() => {
    setPreset(selectedPreset || 'All');
  }, [selectedPreset]);

  const getPresetDates = (presetValue) => {
    const today = dayjs();

    switch (presetValue) {
      case 'thisMonth':
        return {
          start: today.startOf('month'),
          end: today.endOf('month'),
        };
      case 'lastMonth':
        return {
          start: today.subtract(1, 'month').startOf('month'),
          end: today.subtract(1, 'month').endOf('month'),
        };
      case 'thisYear':
        return {
          start: today.startOf('year'),
          end: today.endOf('year'),
        };
      case 'last3Months':
        return {
          start: today.subtract(3, 'month').startOf('month'),
          end: today.subtract(1, 'month').endOf('month'),
        };
      case 'last6Months':
        return {
          start: today.subtract(6, 'month').startOf('month'),
          end: today.subtract(1, 'month').endOf('month'),
        };
      case 'lastYear':
        return {
          start: today.subtract(1, 'year').startOf('year'),
          end: today.subtract(1, 'year').endOf('year'),
        };
      case 'custom':
        return {
          start: startDate ? dayjs(startDate) : null,
          end: endDate ? dayjs(endDate) : null,
        };
      default:
        return {
          start: null,
          end: null,
        };
    }
  };

  const handlePresetChange = (event) => {
    const selectedPreset = event.target.value;
    setPreset(selectedPreset);

    if (selectedPreset !== 'custom') {
      const { start, end } = getPresetDates(selectedPreset);
      onDateRangeChange({
        startDate: start ? start.format('YYYY-MM-DD') : null,
        endDate: end ? end.format('YYYY-MM-DD') : null,
        preset: selectedPreset,
      });
    } else {
      onDateRangeChange({
        startDate: startDate || null,
        endDate: endDate || null,
        preset: selectedPreset,
      });
    }
  };

  const handleStartDateChange = (newDate) => {
    const formattedDate = newDate ? dayjs(newDate).format('YYYY-MM-DD') : null;
    onDateRangeChange({
      startDate: formattedDate,
      endDate,
      preset: 'custom',
    });
    if (preset !== 'custom') {
      setPreset('custom');
    }
  };

  const handleEndDateChange = (newDate) => {
    const formattedDate = newDate ? dayjs(newDate).format('YYYY-MM-DD') : null;
    onDateRangeChange({
      startDate,
      endDate: formattedDate,
      preset: 'custom',
    });
    if (preset !== 'custom') {
      setPreset('custom');
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', ...sx }}>
      <Box width="100%" height={'100%'}>
        <FormControl fullWidth size="small" sx={{ height: '100%' }}>
          <InputLabel id="date-range-preset-label">{label}</InputLabel>
          <Select
            labelId="date-range-preset-label"
            id="date-range-preset-select"
            value={preset}
            label={label}
            sx={{ height: '100%' }}
            onChange={handlePresetChange}
            onOpen={onOpen}
            onClose={onClose}
          >
            <MenuItem value="All">All Time</MenuItem>
            <MenuItem value="thisMonth">This Month</MenuItem>
            <MenuItem value="lastMonth">Last Month</MenuItem>
            <MenuItem value="thisYear">This Year</MenuItem>
            <MenuItem value="last3Months">Last 3 Months</MenuItem>
            <MenuItem value="last6Months">Last 6 Months</MenuItem>
            <MenuItem value="lastYear">Last Year</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {preset === 'custom' && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate ? dayjs(startDate) : null}
            maxDate={dayjs()}
            onChange={handleStartDateChange}
            renderInput={(params) => <TextField {...params} size="small" sx={{ width: 150 }} />}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 300 },
              },
            }}
          />
          <DatePicker
            label="End Date"
            value={endDate ? dayjs(endDate) : null}
            minDate={startDate ? dayjs(startDate) : null}
            maxDate={dayjs()}
            onChange={handleEndDateChange}
            renderInput={(params) => <TextField {...params} size="small" sx={{ width: 150 }} />}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 300 },
              },
            }}
          />
        </LocalizationProvider>
      )}
    </Stack>
  );
};

export default DateRangeFilter;
